import { useState } from "react";
import { X, Calendar, Megaphone, Lightbulb, Sparkles, ExternalLink, Copy, Check } from "lucide-react";
import { useActiveAnnouncements, useDismissAnnouncement, type Announcement, type AnnouncementType } from "@/hooks/useAnnouncements";
import { Button } from "@/components/ui/button";
import { format, formatDistanceToNow, isPast, isToday } from "date-fns";
import { useToast } from "@/hooks/use-toast";

const ICONS: Record<AnnouncementType, React.ReactNode> = {
  event: <Calendar className="w-4 h-4" />,
  banner: <Megaphone className="w-4 h-4" />,
  tip: <Lightbulb className="w-4 h-4" />,
  update: <Sparkles className="w-4 h-4" />,
};

const STYLES: Record<AnnouncementType, { bg: string; border: string; text: string; iconBg: string }> = {
  event: {
    bg: "bg-gradient-to-r from-violet-50 to-purple-50",
    border: "border-violet-200/60",
    text: "text-violet-900",
    iconBg: "bg-violet-100 text-violet-600",
  },
  banner: {
    bg: "bg-gradient-to-r from-blue-50 to-sky-50",
    border: "border-blue-200/60",
    text: "text-blue-900",
    iconBg: "bg-blue-100 text-blue-600",
  },
  tip: {
    bg: "bg-gradient-to-r from-amber-50 to-yellow-50",
    border: "border-amber-200/60",
    text: "text-amber-900",
    iconBg: "bg-amber-100 text-amber-600",
  },
  update: {
    bg: "bg-gradient-to-r from-emerald-50 to-teal-50",
    border: "border-emerald-200/60",
    text: "text-emerald-900",
    iconBg: "bg-emerald-100 text-emerald-600",
  },
};

const formatEventDate = (dateString: string | null): string | null => {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  
  if (isPast(date)) {
    return null; // Don't show past events
  }
  
  if (isToday(date)) {
    return `Today at ${format(date, "h:mm a")}`;
  }
  
  const distance = formatDistanceToNow(date, { addSuffix: true });
  return `${format(date, "MMM d")} at ${format(date, "h:mm a")} (${distance})`;
};

// Check if event is imminent (within 15 minutes before to 1 hour after)
const isEventImminent = (eventDate: string | null): boolean => {
  if (!eventDate) return false;
  const date = new Date(eventDate);
  const now = new Date();
  const minutesUntilEvent = (date.getTime() - now.getTime()) / (1000 * 60);
  return minutesUntilEvent <= 15 && minutesUntilEvent >= -60;
};

// Format date for Google Calendar (YYYYMMDDTHHmmssZ)
const formatCalendarDate = (date: Date): string => {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

// Generate Google Calendar URL
const generateGoogleCalendarUrl = (announcement: Announcement): string => {
  if (!announcement.event_date) return '';
  
  const eventDate = new Date(announcement.event_date);
  const endDate = announcement.ends_at 
    ? new Date(announcement.ends_at) 
    : new Date(eventDate.getTime() + 60 * 60 * 1000); // Default 1 hour
  
  const details = [
    announcement.content || '',
    '',
    announcement.cta_url ? `Join: ${announcement.cta_url}` : ''
  ].filter(Boolean).join('\n');
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: announcement.title,
    dates: `${formatCalendarDate(eventDate)}/${formatCalendarDate(endDate)}`,
    details: details,
  });
  
  if (announcement.cta_url) {
    params.set('location', announcement.cta_url);
  }
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Clean markdown and create a summary for display
const cleanContentSummary = (content: string | null): string => {
  if (!content) return '';
  
  // Remove markdown formatting
  let cleaned = content
    .replace(/\*\*/g, '') // Remove bold markers
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .replace(/•/g, '') // Remove bullet points
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
  
  // Get first meaningful sentence or truncate
  const firstSentence = cleaned.match(/^[^.!?]+[.!?]/)?.[0];
  if (firstSentence && firstSentence.length <= 100) {
    return firstSentence.trim();
  }
  
  // Truncate to ~100 chars at word boundary
  if (cleaned.length > 100) {
    cleaned = cleaned.slice(0, 100).replace(/\s+\S*$/, '') + '…';
  }
  
  return cleaned;
};

interface AnnouncementCardProps {
  announcement: Announcement;
  onDismiss: () => void;
}

const AnnouncementCard = ({ announcement, onDismiss }: AnnouncementCardProps) => {
  const style = STYLES[announcement.type];
  const icon = ICONS[announcement.type];
  const eventDateFormatted = formatEventDate(announcement.event_date);
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const isImminent = announcement.type === "event" && isEventImminent(announcement.event_date);

  const handleJoinClick = () => {
    if (announcement.cta_url) {
      window.open(announcement.cta_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleAddToCalendar = () => {
    const calendarUrl = generateGoogleCalendarUrl(announcement);
    if (calendarUrl) {
      window.open(calendarUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleCopyLink = async () => {
    if (announcement.cta_url) {
      try {
        await navigator.clipboard.writeText(announcement.cta_url);
        setCopied(true);
        toast({
          title: "Link copied!",
          description: "Meeting link copied to clipboard",
        });
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast({
          title: "Failed to copy",
          description: "Please copy the link manually",
          variant: "destructive",
        });
      }
    }
  };

  const handleCTAClick = () => {
    if (announcement.cta_url) {
      if (announcement.cta_url.startsWith("http")) {
        window.open(announcement.cta_url, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = announcement.cta_url;
      }
    }
  };

  return (
    <div
      className={`
        ${style.bg} ${style.border} border rounded-xl p-4 
        shadow-sm animate-fade-in relative group
      `}
    >
      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 p-1.5 rounded-full opacity-50 hover:opacity-100 
          hover:bg-white/50 transition-all"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-slate-500" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        {/* Icon */}
        <div className={`p-2 rounded-lg ${style.iconBg} flex-shrink-0`}>
          {icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold ${style.text} text-sm leading-tight`}>
            {announcement.title}
          </h3>
          
          {announcement.content && (
            <p className="text-slate-600 text-sm mt-1">
              {cleanContentSummary(announcement.content)}
            </p>
          )}

          {/* Event date */}
          {announcement.type === "event" && eventDateFormatted && (
            <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {eventDateFormatted}
            </p>
          )}

          {/* Event-specific CTAs */}
          {announcement.type === "event" && announcement.cta_url && (
            <div className="flex items-center gap-3 mt-2">
              {isImminent ? (
                <Button
                  size="sm"
                  onClick={handleJoinClick}
                  className="h-7 px-3 text-xs font-medium bg-violet-600 hover:bg-violet-700 text-white"
                >
                  Join Now
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleAddToCalendar}
                  className="h-7 px-3 text-xs font-medium bg-violet-600 hover:bg-violet-700 text-white"
                >
                  <Calendar className="w-3 h-3 mr-1" />
                  Add to Calendar
                </Button>
              )}
              <button
                onClick={handleCopyLink}
                className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2 flex items-center gap-1 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy link
                  </>
                )}
              </button>
            </div>
          )}

          {/* Non-event CTA Button */}
          {announcement.type !== "event" && announcement.cta_text && announcement.cta_url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCTAClick}
              className={`mt-2 h-7 px-3 text-xs font-medium ${style.text} hover:bg-white/50`}
            >
              {announcement.cta_text}
              {announcement.cta_url.startsWith("http") && (
                <ExternalLink className="w-3 h-3 ml-1" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

interface AnnouncementBannerProps {
  maxAnnouncements?: number;
  className?: string;
}

export const AnnouncementBanner = ({ 
  maxAnnouncements = 2, 
  className = "" 
}: AnnouncementBannerProps) => {
  const { data: announcements, isLoading } = useActiveAnnouncements();
  const dismissMutation = useDismissAnnouncement();
  const [locallyDismissed, setLocallyDismissed] = useState<Set<string>>(new Set());

  if (isLoading || !announcements?.length) {
    return null;
  }

  // Filter out locally dismissed (for non-logged-in users or optimistic updates)
  const visibleAnnouncements = announcements
    .filter((a) => !locallyDismissed.has(a.id))
    .slice(0, maxAnnouncements);

  if (!visibleAnnouncements.length) {
    return null;
  }

  const handleDismiss = (announcementId: string) => {
    setLocallyDismissed((prev) => new Set(prev).add(announcementId));
    dismissMutation.mutate(announcementId);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {visibleAnnouncements.map((announcement) => (
        <AnnouncementCard
          key={announcement.id}
          announcement={announcement}
          onDismiss={() => handleDismiss(announcement.id)}
        />
      ))}
    </div>
  );
};

export default AnnouncementBanner;
